/**
 * Camera utility functions
 */

/**
 * Get camera stream with specified constraints
 * @param {Object} constraints - MediaStream constraints
 * @param {string} constraints.facingMode - Camera facing mode ('user' or 'environment')
 * @param {number} constraints.width - Video width
 * @param {number} constraints.height - Video height
 * @returns {Promise<MediaStream>} Camera stream
 * @throws {Error} If camera access fails
 */
export const getCameraStream = async (constraints = {}) => {
  try {
    const defaultConstraints = {
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: 'user'
      }
    };

    const mergedConstraints = {
      video: { ...defaultConstraints.video, ...constraints }
    };

    return await navigator.mediaDevices.getUserMedia(mergedConstraints);
  } catch (error) {
    console.error('Camera access error:', error);
    throw new Error('Failed to access camera. Please ensure camera permissions are granted.');
  }
};

/**
 * Stop camera stream
 * @param {MediaStream|null} stream - Stream to stop
 */
export const stopCameraStream = (stream) => {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
  }
};

/**
 * Play video element with stream
 * @param {HTMLVideoElement} videoElement - Video element
 * @param {MediaStream} stream - Camera stream
 * @returns {Promise<void>}
 */
export const playVideoWithStream = async (videoElement, stream) => {
  if (!videoElement) {
    throw new Error('Video element is required');
  }

  // Clear any existing srcObject to prevent conflicts
  if (videoElement.srcObject) {
    videoElement.srcObject.getTracks().forEach(track => track.stop());
  }

  videoElement.srcObject = stream;
  
  try {
    await videoElement.play();
  } catch (error) {
    // Handle AbortError gracefully - this is expected when video is removed from DOM
    if (error.name === 'AbortError' || error.message.includes('interrupted')) {
      // Clean up the stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      throw error;
    }
    throw error;
  }
};

/**
 * Capture frame from video element
 * @param {HTMLVideoElement} videoElement - Video element
 * @param {number} quality - Image quality (0-1)
 * @returns {string} Data URL of captured image
 */
export const captureFrame = (videoElement, quality = 0.9) => {
  if (!videoElement) {
    throw new Error('Video element is required');
  }

  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;
  
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
  
  return canvas.toDataURL('image/jpeg', quality);
};

/**
 * Initialize camera with video element
 * @param {HTMLVideoElement} videoElement - Video element
 * @param {Object} constraints - Camera constraints
 * @returns {Promise<MediaStream>} Camera stream
 */
export const initializeCamera = async (videoElement, constraints = {}) => {
  const stream = await getCameraStream(constraints);
  await playVideoWithStream(videoElement, stream);
  return stream;
};
